import { ESInterface } from "src/compentsModus/ESInterface";
import { VersionedObjectPersistence } from "src/common/managers/ESVersionedPersistance";
import { PLADataService, PLAElementRecord, PLAProject, PLAProjectService, PLARecord, PLARelationRecord } from "./PLAState";
import { getPLAElementData } from "./PLAElementData";

const PLA_MAPPING_TOTAL_FIELDS_LIMIT = 10000;
const projectPersistenceCache = new Map<string, PLAProjectPersistence>();
const recordPersistenceCache = new Map<string, PLARecordPersistence>();

type PLARecordSearchPage = {
   _scroll_id?: string;
   hits: { total: { value: number; relation: string }; hits: { _version: number; _source: PLARecord }[] };
};

async function saveVersionCheckedPLAObject<T extends { _version?: number }>(persistence: VersionedObjectPersistence<T>, id: string, value: T, expectedVersion: number): Promise<void> {
   if (!Number.isInteger(expectedVersion) || expectedVersion < 1) throw new Error("A valid document version is required for a version-checked save");
   await persistence.dw.workQueue.queueWork(async () => {
      const { _version, ...document } = value;
      const saved = await persistence.es.runCommand("POST", `${persistence.esIndexName}/_doc/${encodeURIComponent(id)}?version=${expectedVersion}&version_type=internal`, { ...document, viamapUserId: persistence.userId });
      if (!Number.isInteger(saved._version)) throw new Error("Elasticsearch did not return the saved document version");
      persistence.versions.setObjectVersion(id, saved._version);
   });
}

async function allowLargeExistingMappings(es: ESInterface, indexName: string): Promise<void> {
   await es.runCommand("PUT", `${indexName}/_settings`, {
      "index.mapping.total_fields.limit": PLA_MAPPING_TOTAL_FIELDS_LIMIT
   });
}

export class PLAProjectPersistence extends VersionedObjectPersistence<PLAProject> {
   private static dynamicDictionaryMappings = {
      properties: {
         allowedUsersWithRoles: { type: "object", dynamic: false },
         allowedOrganizationsWithRoles: { type: "object", dynamic: false },
         mainHierarchicalRelationTypeId: { type: "keyword" },
         cubeTimeDimensionId: { type: "keyword" },
         settings: { type: "object", dynamic: false },
         elementTypes: { type: "object", dynamic: false },
         relationTypes: { type: "object", dynamic: false }
      }
   };
   public static indexMappings = {
      properties: {
         id: { type: "keyword" },
         name: { type: "text", fields: { keyword: { type: "keyword" } } },
         description: { type: "text" },
         active: { type: "boolean" },
         ownerUser: { type: "keyword" },
         ownerOrganization: { type: "keyword" },
         esIndexName: { type: "keyword" },
         mainHierarchicalRelationTypeId: { type: "keyword" },
         cubeTimeDimensionId: { type: "keyword" },
         protected: { type: "boolean" },
         allowedUsersWithRoles: { type: "object", dynamic: false },
         allowedOrganizationsWithRoles: { type: "object", dynamic: false },
         settings: { type: "object", dynamic: false },
         elementTypes: { type: "object", dynamic: false },
         relationTypes: { type: "object", dynamic: false },
         viamapUserId: { type: "keyword" },
         lastChangedTimestamp: { type: "date" }
      }
   };
   private static idGetter = (object: PLAProject) => object.id;

   constructor(objectsESIndexName: string, userId: string) {
      super(objectsESIndexName, userId, PLAProjectPersistence.indexMappings, PLAProjectPersistence.idGetter);
   }

   async ensureProjectMapping(): Promise<void> {
      if (!await this.es.indexExists(this.esIndexName)) {
         await this.es.createIndex(this.esIndexName, { mappings: { ...PLAProjectPersistence.indexMappings, _meta: { CreatedBy: this.userId } } });
         return;
      }
      await allowLargeExistingMappings(this.es, this.esIndexName).catch(() => undefined);
      await this.es.runCommand("PUT", `${this.esIndexName}/_mapping`, PLAProjectPersistence.dynamicDictionaryMappings);
   }
}

export class PLARecordPersistence extends VersionedObjectPersistence<PLARecord> {
   private static dynamicDictionaryMappings = {
      properties: {
         sortingNumber: { type: "double" },
         hasDocuments: { type: "boolean" },
         attributes: { type: "object", dynamic: false }
      }
   };
   public static indexMappings = {
      properties: {
         recordType: { type: "keyword" },
         id: { type: "keyword" },
         elementType: { type: "keyword" },
         sortingNumber: { type: "double" },
         hasDocuments: { type: "boolean" },
         relationType: { type: "keyword" },
         fromId: { type: "keyword" },
         toId: { type: "keyword" },
         geoPoint: { type: "geo_point" },
         geoShape: { type: "geo_shape" },
         attributes: { type: "object", dynamic: false },
         viamapUserId: { type: "keyword" },
         lastChangedTimestamp: { type: "date" }
      }
   };
   private static idGetter = (object: PLARecord) => object.id;

   constructor(objectsESIndexName: string, userId: string) {
      super(objectsESIndexName, userId, PLARecordPersistence.indexMappings, PLARecordPersistence.idGetter);
   }

   async ensureRecordMapping(): Promise<void> {
      if (!await this.es.indexExists(this.esIndexName)) {
         await this.es.createIndex(this.esIndexName, { mappings: { ...PLARecordPersistence.indexMappings, _meta: { CreatedBy: this.userId } } });
         return;
      }
      await allowLargeExistingMappings(this.es, this.esIndexName).catch(() => undefined);
      await this.es.runCommand("PUT", `${this.esIndexName}/_mapping`, PLARecordPersistence.dynamicDictionaryMappings);
   }

   refreshIndex(): Promise<void> {
      return this.es.runCommand("GET", `${this.esIndexName}/_refresh`, undefined).then(() => undefined);
   }

   async listAllRecords(): Promise<PLARecord[]> {
      const records: PLARecord[] = [];
      const ids = new Set<string>();
      let scrollId: string | undefined;
      try {
         let page: PLARecordSearchPage = await this.es.runCommand("POST", `${this.esIndexName}/_search?scroll=1m`, { size: 5000, sort: ["_doc"], version: true, track_total_hits: true, query: { match_all: {} } });
         scrollId = page._scroll_id;
         const total = page.hits?.total?.value;
         if (!Number.isInteger(total) || total < 0 || page.hits.total.relation !== "eq") throw new Error("Could not determine the complete project record count");
         do {
            scrollId = page._scroll_id || scrollId;
            if (!Array.isArray(page.hits?.hits)) throw new Error("Invalid project record search response");
            for (const hit of page.hits.hits) {
               const record = hit._source;
               if (!record || typeof record.id !== "string" || !["ELEMENT", "RELATION"].includes(record.recordType) || !Number.isInteger(hit._version) || hit._version < 1) throw new Error("Invalid versioned project record");
               if (ids.has(record.id)) throw new Error(`Duplicate project record: ${record.id}`);
               ids.add(record.id);
               records.push({ ...record, _version: hit._version });
               this.versions.setObjectVersion(record.id, hit._version);
            }
            if (records.length === total) break;
            if (records.length > total || page.hits.hits.length === 0 || !scrollId) throw new Error("Project record search was incomplete");
            page = await this.es.runCommand("POST", "_search/scroll", { scroll: "1m", scroll_id: scrollId });
         } while (records.length < total);
         return records;
      } finally {
         if (scrollId) await this.es.runCommand("DELETE", "_search/scroll", { scroll_id: [scrollId] });
      }
   }
}

export class PLAProjectServiceImpl implements PLAProjectService {
   private persistence: PLAProjectPersistence;

   constructor(projectsElasticIndex: string, userId: string) {
      const cacheKey = `${projectsElasticIndex}\u0000${userId}`;
      const cached = projectPersistenceCache.get(cacheKey);
      this.persistence = cached || new PLAProjectPersistence(projectsElasticIndex, userId);
      if (!cached) projectPersistenceCache.set(cacheKey, this.persistence);
   }

   loadProjects(): Promise<PLAProject[]> {
      return this.persistence.listObjects();
   }

   loadProject(projectId: string): Promise<PLAProject> {
      return this.persistence.getObject(projectId);
   }

   saveProject(project: PLAProject, expectedVersion?: number): Promise<void> {
      if (expectedVersion !== undefined) return saveVersionCheckedPLAObject(this.persistence, project.id, project, expectedVersion)
         .then(() => this.persistence.es.runCommand("GET", `${this.persistence.esIndexName}/_refresh`, undefined))
         .then(() => undefined);
      return this.persistence.ensureProjectMapping()
         .catch(() => undefined)
         .then(() => this.persistence.saveObject(project));
   }

   async updateCubeTimeDimension(projectId: string, dimensionId: string): Promise<void> {
      await this.persistence.ensureProjectMapping();
      await this.persistence.es.runCommand("POST", `${this.persistence.esIndexName}/_update/${encodeURIComponent(projectId)}`, {
         doc: { cubeTimeDimensionId: dimensionId }
      });
      await this.persistence.es.runCommand("GET", `${this.persistence.esIndexName}/_refresh`, undefined);
   }

   deleteProject(projectId: string): Promise<void> {
      return this.persistence.deleteObject(projectId);
   }

   async createProject(project: PLAProject): Promise<void> {
      await this.saveProject(project);
      const es = new ESInterface(this.persistence.esIndexName);
      await es.createIndex(project.esIndexName, { mappings: { ...PLARecordPersistence.indexMappings, _meta: { CreatedBy: this.persistence.userId } } });
   }
}

export class PLADataServiceImpl implements PLADataService {
   private persistence: PLARecordPersistence;

   constructor(index: string, userId: string) {
      const cacheKey = `${index}\u0000${userId}`;
      const cached = recordPersistenceCache.get(cacheKey);
      this.persistence = cached || new PLARecordPersistence(index, userId);
      if (!cached) recordPersistenceCache.set(cacheKey, this.persistence);
   }

   async loadElements(): Promise<PLAElementRecord[]> {
      const records = await this.loadRecords();
      return records.filter((record): record is PLAElementRecord => record.recordType === "ELEMENT");
   }

   async loadRelations(): Promise<PLARelationRecord[]> {
      const records = await this.loadRecords();
      return records.filter((record): record is PLARelationRecord => record.recordType === "RELATION");
   }

   loadRecords(): Promise<PLARecord[]> {
      return this.persistence.listObjects();
   }

   loadAllRecords(): Promise<PLARecord[]> {
      return this.persistence.listAllRecords();
   }

   loadRecord(recordId: string): Promise<PLARecord> {
      return this.persistence.getObject(recordId);
   }

   async updateElementDocumentState(elementId: string, hasDocuments: boolean): Promise<void> {
      await this.persistence.ensureRecordMapping();
      const current = await this.persistence.getObject(elementId);
      if (current.recordType !== "ELEMENT") throw new Error("The element record no longer exists");
      if (Boolean(current.hasDocuments) === hasDocuments) return;
      await saveVersionCheckedPLAObject(this.persistence, elementId, { ...current, hasDocuments }, current._version || 0);
      await this.persistence.refreshIndex();
   }

   saveElement(element: PLAElementRecord, expectedVersion?: number): Promise<void> {
      element = getPLAElementData(element);
      if (expectedVersion !== undefined) return saveVersionCheckedPLAObject(this.persistence, element.id, element, expectedVersion)
         .then(() => this.persistence.refreshIndex());
      return this.persistence.ensureRecordMapping()
         .catch(() => undefined)
         .then(() => this.persistence.saveObject(element))
         .then(() => this.persistence.refreshIndex().catch(() => undefined));
   }

   saveRelation(relation: PLARelationRecord): Promise<void> {
      return this.persistence.ensureRecordMapping()
         .catch(() => undefined)
         .then(() => this.persistence.saveObject(relation))
         .then(() => this.persistence.refreshIndex().catch(() => undefined));
   }

   deleteElement(elementId: string): Promise<void> {
      return this.persistence.deleteObject(elementId)
         .then(() => this.persistence.refreshIndex().catch(() => undefined));
   }

   deleteRelation(relationId: string): Promise<void> {
      return this.persistence.deleteObject(relationId)
         .then(() => this.persistence.refreshIndex().catch(() => undefined));
   }
}
